import { Controller, useFormContext } from 'react-hook-form'

import { Select, Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { Expensable } from 'modules/expenses/types/Expensable'

const FIELD_NAME = 'projects'

export interface FormProject {
  projectWorkerId: number | null
  subprojectId: number | null
}

interface Props extends WithFieldPermissionsProps, WithCustomizationFieldsProps {
  projectWorkers: ProjectWorker[]
  loading: boolean
  defaultValue?: FormProject
  dirtyEmployee?: boolean
}

const defaultToProjectWorker = (
  defaultValue?: NonNullable<Expensable['projects']>[number]
): ProjectWorker | null => {
  if (!defaultValue?.projectWorker?.imputableProject) return null

  const { projectWorker, subproject } = defaultValue
  const { imputableProject } = projectWorker

  if (!imputableProject) return null

  return {
    id: projectWorker.id,
    assigned: projectWorker.assigned,
    imputableProject: {
      ...imputableProject,
      subprojects: subproject ? [subproject] : [],
    },
  }
}

const projectWorkersWithDefault = (
  projectWorkers: ProjectWorker[],
  defaultValue?: NonNullable<Expensable['projects']>[number]
) => {
  const defaultProjectWorker = defaultToProjectWorker(defaultValue)

  if (!defaultProjectWorker || projectWorkers.some(({ id }) => id === defaultProjectWorker.id)) {
    return projectWorkers
  }

  return [...projectWorkers, defaultProjectWorker]
}

const ProjectField = ({
  projectWorkers: originalProjectWorkers,
  loading,
  dirtyEmployee,
  isFieldMandatory,
  isFieldReadOnly,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  return (
    <Controller
      name={FIELD_NAME}
      control={control}
      render={({ field }) => {
        const currentDefault = dirtyEmployee ? undefined : field.value?.[0]
        const projectWorkers = projectWorkersWithDefault(originalProjectWorkers, currentDefault)

        if (!projectWorkers.length) return <></>

        const projectWorkerId: string | undefined = field.value?.[0]?.projectWorker?.id ?? undefined
        const subprojectId: string | undefined = field.value?.[0]?.subproject?.id ?? undefined

        const project = projectWorkers.find(({ id }) => id === projectWorkerId)?.imputableProject
        const subprojects = project?.subprojects ?? []
        const subproject = subprojects.find(({ id }) => id === subprojectId)

        const errorMessage = castError(errors?.projects)
        const showErrorMessage = (name?: string | null) => {
          if (errorMessage && !name) return errorMessage
        }

        const projectOptions = projectWorkers.map(({ id, imputableProject }) => ({
          label: imputableProject?.name ?? '',
          value: String(id),
        }))

        const subprojectOptions = subprojects.map(({ id, name }) => ({
          label: name,
          value: String(id ?? ''),
        }))

        const handleProjectChange = (selectedValue: string) => {
          const newProjectWorkerId = Number.parseInt(selectedValue, 10)
          const transformedValue = isNaN(newProjectWorkerId)
            ? []
            : [
                {
                  projectWorker: { id: newProjectWorkerId },
                  subproject: { id: null },
                },
              ]
          field.onChange(transformedValue)
        }

        const handleSubprojectChange = (selectedValue: string) => {
          const newSubprojectId = Number.parseInt(selectedValue, 10)
          field.onChange([
            {
              projectWorker: { id: projectWorkerId },
              subproject: { id: isNaN(newSubprojectId) ? null : newSubprojectId },
            },
          ])
        }

        return (
          <Stack gap="6">
            <Select
              label={i18n.t('expenses_v2.forms.fields.projects.project')}
              placeholder={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
              showSearchBox
              searchBoxPlaceholder={i18n.t('common.search')}
              searchEmptyMessage={i18n.t('common.projects_not_found')}
              options={projectOptions}
              value={projectWorkerId != null ? String(projectWorkerId) : undefined}
              onChange={handleProjectChange}
              required={isFieldMandatory}
              disabled={isFieldReadOnly}
              loading={loading}
              error={showErrorMessage(project?.name)}
              clearable={!isFieldMandatory}
            />

            {subprojects.length > 0 && (
              <Select
                label={i18n.t('expenses_v2.forms.fields.projects.subproject')}
                placeholder={i18n.t('expenses_v2.forms.fields.projects.placeholder')}
                showSearchBox
                searchBoxPlaceholder={i18n.t('common.search')}
                searchEmptyMessage={i18n.t('common.projects_not_found')}
                options={subprojectOptions}
                value={subprojectId != null ? String(subprojectId) : undefined}
                onChange={handleSubprojectChange}
                required={isFieldMandatory}
                disabled={isFieldReadOnly}
                loading={loading}
                error={showErrorMessage(subproject?.name)}
                clearable={!isFieldMandatory}
              />
            )}
          </Stack>
        )
      }}
    />
  )
}

export default withCustomizationFields(withFieldPermissions(ProjectField, FIELD_NAME))
